import { DeepPartial } from 'devextreme/core';
import CustomStore from 'devextreme/data/custom_store';
import ArrayStore from 'devextreme/data/array_store';

// Sort type
export enum SortType {
  Asc = 'asc',
  Desc = 'desc',
}

/** Data table source */
export class DataTableSource<TData> {
  private readonly _dataSource: CustomStore<TData, string | number>;
  private readonly _cacheSource: ArrayStore<TData, string | number>;
  private _totalCount: number;

  constructor(key: keyof TData, data: TData[], totalCount?: number) {
    const items = JSON.parse(JSON.stringify(data));

    this._totalCount = totalCount ?? data.length;

    this._cacheSource = new ArrayStore<TData>({
      data: items,
      key: key as string,
    });

    this._dataSource = new CustomStore<TData>({
      key: key.toString(),
      load: async () => {
        return {
          data: (this._cacheSource as any)._array,
          totalCount: this._totalCount,
        };
      },
      insert: async (data) => {
        this._totalCount += 1;
        const item: DeepPartial<TData> = JSON.parse(JSON.stringify(data));
        this._cacheSource.push([{ type: 'insert', data: item }]);

        return data;
      },
      update: async (key, data) => {
        const item: DeepPartial<TData> = JSON.parse(JSON.stringify(data));
        this._cacheSource.push([{ type: 'update', key, data: item }]);

        return { key, ...data };
      },
      remove: async (key) => {
        this._totalCount -= 1;
        this._cacheSource.push([{ type: 'remove', key }]);

        return key;
      },
    });

    this._dataSource.load();
  }

  get dataSource(): CustomStore<TData, string | number> {
    return this._dataSource;
  } // dataSource

  get data(): TData[] {
    return (this._cacheSource as any)._array;
  } // data

  get key(): keyof TData {
    return this._dataSource.key() as keyof TData;
  } // key

  public clear(): void {
    this._cacheSource.clear();
    this._dataSource.remove(Math.random());
  } // key

  public totalCount(count: number): void {
    this._totalCount = count;
  }

  public insert(data: TData[]): void {
    data.forEach((item) => {
      this._dataSource.insert(item);
    });
  } // insert

  public remove(keys: (string | number)[]): void {
    keys.forEach((item) => {
      this._dataSource.remove(item);
    });
  } // remove

  public update(changes: { key: string | number; data: DeepPartial<TData> }[]): void {
    changes.forEach((item) => {
      this._dataSource.update(item.key, item.data);
    });
  } // update
}
